import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { DataType, SourceBookType } from "@dndModels/Types";
import { SourceBook } from "@dndModels";

import MyTTRPGAPI from "@utilities/smart_api";

import StringBox from '@components/input/strings/StringBox';
import FlexBox from '@components/layout/FlexBox';
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import DandDButtonBox from './DandDButtonBoxSimple';

const api = MyTTRPGAPI.getInstance();

interface Props {
  data_type: DataType;
  plural: string;
  onDone: () => void;
}

interface UnownedDoc {
  _id: string;
  light_id: string;
  name: string;
  source_books_old: { type: SourceBookType, id: string, third_party: boolean }[];
}

// No source book (or only "Personal"), and no owner - so no one but an admin can load them.
const UNOWNED_HOMEBREW_FILTER = {
  "$and": [
    { "$or": [{ "owner_id": "" }, { "owner_id": { "$exists": false } }] },
    { "$or": [
      { "source_books": { "$size": 0 } },
      { "source_books": { "$exists": false } },
      { "source_books.id": "Personal" },
    ]},
  ],
};

// Admin tool: finds Personal Homebrew with no owner straight from the database, and
// moves them into a source book.
export default function UnownedHomebrewTool({ data_type, plural, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [docs, setDocs] = useState<UnownedDoc[] | null>(null);
  const [name_filter, setNameFilter] = useState("");
  const [book_id, setBookId] = useState("");
  const [book_type, setBookType] = useState<SourceBookType | "">("");
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState<{ done: number, total: number, failed: string[] } | null>(null);

  const book = api.getDataTypeFromHash<SourceBook>("source_book").find(o => o._id === book_id);
  const book_name = book ? (book.abbreviation || book.name) : "";
  const lower = name_filter.trim().toLowerCase();
  const shown = (docs ?? []).filter(o => lower === "" || o.name.toLowerCase().includes(lower));
  const running = !!saving && saving.done < saving.total;

  const find = async () => {
    setLoading(true);
    setError("");
    setSaving(null);
    try {
      const raw = await api.getAllObjectsFromAPIRaw({ data_type, collection: data_type, filter: UNOWNED_HOMEBREW_FILTER });
      setDocs(raw
        .map((o: any) => ({ _id: o._id, light_id: o.light_id ?? "", name: o.name ?? "(unnamed)", source_books_old: o.source_books ?? [] }))
        .sort((a: UnownedDoc, b: UnownedDoc) => a.name.localeCompare(b.name)));
    } catch (ex: any) {
      console.error(ex);
      setError(ex?.message ?? "Failed to load");
    }
    setLoading(false);
  };

  const moveShown = async () => {
    setConfirm(false);
    const list = [...shown];
    const failed: string[] = [];
    const moved: string[] = [];
    const source_books = [{ id: book_id, type: book ? book.source_book_type : "Source Book" as SourceBookType, third_party: book ? book.third_party : false }];
    setSaving({ done: 0, total: list.length, failed });
    for (let i = 0; i < list.length; ++i) {
      const doc = list[i];
      try {
        // Replaces what's there - they have no real source book, just "Personal" or nothing.
        if (await api.updateSourceBooks(data_type, doc, source_books)) {
          moved.push(doc._id);
        } else {
          failed.push(doc.name);
        }
      } catch (ex) {
        console.error(ex);
        failed.push(doc.name);
      }
      setSaving({ done: i + 1, total: list.length, failed: [...failed] });
    }
    setDocs(prev => (prev ?? []).filter(o => !moved.includes(o._id)));
    onDone();
  };

  if (!open) {
    return (
      <FlexBox>
        <DandDButtonBox name="Unowned Personal Homebrew" onClick={() => { setOpen(true); find(); }} />
      </FlexBox>
    );
  }
  return (
    <FlexBox direction="column" className="padded" style={{ border: "1px solid rgba(128,128,128,0.5)", borderRadius: 4 }}>
      <span className="header">Unowned Personal Homebrew { plural }</span>
      <span>
        { plural } with no source book (or only Personal Homebrew) and no owner, read straight from the database. Nobody
        but an admin can load these. Narrow the list by name, then move the ones shown into a source book.
      </span>
      { loading && <span>Loading...</span> }
      { error !== "" && <span style={{ color: "red" }}>{ error }</span> }
      { docs &&
        <>
          <span>{ docs.length } found{ lower !== "" ? `, ${shown.length} shown` : "" }.</span>
          <StringBox name="Filter by name" value={name_filter} disabled={running || confirm}
            onChange={(value: string) => setNameFilter(value)}
          />
          <FlexBox direction="column" style={{ maxHeight: 300, overflowY: "auto" }}>
            { shown.map(o => (
              <span key={o._id}>
                <Link to={`/${data_type}/${o._id}`}>{ o.name }</Link>
                <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                  {" "}[{ o.source_books_old.length === 0 ? "no source books" : o.source_books_old.map(sb => sb.id).join(", ") }]
                </span>
              </span>
            ))}
          </FlexBox>
          <SelectSourceBookBox name="Move To" id={book_id} type={book_type} disabled={running || confirm}
            onChange={(type, id) => { setBookType(type); setBookId(id); setSaving(null); }}
          />
        </>
      }
      { confirm ?
        <FlexBox direction="row">
          <FlexBox>
            <span>Move { shown.length } { plural } into { book_name }? They&apos;ll be in { book_name } only.</span>
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name={`Yes, Move ${shown.length}`} onClick={moveShown} />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Cancel" onClick={() => setConfirm(false)} />
          </FlexBox>
        </FlexBox>
      :
        <FlexBox direction="row">
          <FlexBox xs_expand={0}>
            <DandDButtonBox name={book_id === "" ? "Choose a Source Book" : `Move ${shown.length} Shown to ${book_name}`}
              disabled={running || loading || book_id === "" || shown.length === 0}
              onClick={() => setConfirm(true)}
            />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Reload" disabled={running || loading} onClick={find} />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Close" disabled={running} onClick={() => { setOpen(false); setSaving(null); }} />
          </FlexBox>
        </FlexBox>
      }
      { saving &&
        <span>
          { running ?
            `Moving to ${book_name} ${saving.done} / ${saving.total}...` :
            `Moved ${saving.total - saving.failed.length} of ${saving.total} to ${book_name}.${saving.failed.length > 0 ? ` Failed: ${saving.failed.join(", ")}` : ""}`
          }
        </span>
      }
    </FlexBox>
  );
}
